'use client';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react/macro';
import z from 'zod';

import Check from '@rover/icons/svg/check.svg';
import { Button, Flex, Paragraph, Text } from '@rover/kibble/core';
import { DataTypeOfZodSchema, Form, useKibbleForm } from '@rover/kibble/forms';
import TextField from '@rover/kibble/forms/fields/TextField';
import ImageHeader from '@rover/kibble/patterns/ImageHeader';
import useTheme from '@rover/kibble/themes/useTheme';
import { resendCountdownLabel } from '@rover/react-lib/src/utils/resendCountdownLabel';
import { useApiAccountEmailVerificationGenerateCreate } from '@rover/rsdk/src/apiClient/latest/api-account-email-verification-generate-create';
import { useApiAccountEmailVerificationRedeemCreate } from '@rover/rsdk/src/apiClient/latest/api-account-email-verification-redeem-create';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';
import { useAuthedUserSummary } from '@rover/rsdk/src/modules/Data/Core/CurrentUser';
import { isReactNative } from '@rover/rsdk/src/modules/Env/react';
import { AnalyticsEvent } from '@rover/types/src/Analytics';
import { EmailVerificationStatus } from '@rover/types/src/EmailVerification';

import verifyEmailImage from './images/verify.png';
import type {
  EmailVerificationEventProperties,
  EmailVerificationObservabilityTags,
} from './analyticsEvents';
import {
  VerifyEmailAddCode,
  VerifyEmailLogout,
  VerifyEmailPageLoad,
  VerifyEmailResendCode,
  VerifyEmailSkip,
  VerifyEmailSubmit,
} from './analyticsEvents';
import announceVerified from './announceVerified';
import { descriptionText, headerText } from './content';
import PageChrome from './PageChrome';
import useVerificationExit from './useVerificationExit';
import { readErrorMessage } from './utils';

/** Seconds the resend button stays disabled after a code is sent. */
const RESEND_COOLDOWN_SECONDS = 10;

/**
 * How long the "Email verified!" confirmation stays up before the page exits. It has to
 * outlast the announcement, not just the glance, so this covers live-region pickup plus
 * the spoken message rather than the time it takes to read. Balanced against holding
 * everyone on a success screen -- shortening it further cuts the announcement off.
 */
export const VERIFIED_DISMISS_MS = 2000;

const verifiedMessage = msg`Email verified!`;

/** Shown when the request to mail a code fails and the endpoint gives no reason. */
const sendFailedMessage = msg`We couldn't send a code to your email. Please try again.`;

export type Props = {
  /**
   * Overrides the account-level verification status. Web passes this when the
   * referring page escalates an otherwise optional flow to compulsory; otherwise
   * the status reported by the API is the source of truth.
   */
  flow?: EmailVerificationStatus;
  /** The page or screen the user arrived from, reported as `flowSource`. */
  source?: string;
  /** Web only: where to return after verifying or skipping. */
  returnUrl?: string;
  /** Web only: where to land after logging out of the compulsory flow. */
  nextUrl?: string;
  /**
   * Web only: leaves the flow without navigating, for a host that closes itself --
   * the Django blocking modal listens for its own acknowledgement. Wins over `returnUrl`.
   */
  onExit?: () => void;
  /** Web only: drops the page's outer spacing when a host already frames the content. */
  isEmbedded?: boolean;
};

const EmailVerificationPage = ({
  flow,
  source = '',
  returnUrl,
  nextUrl,
  onExit,
  isEmbedded,
}: Props): JSX.Element => {
  const { t, i18n } = useLingui();
  const { colors } = useTheme();
  const { exit, logout } = useVerificationExit({ returnUrl, nextUrl, onExit });

  const { data: userSummary } = useAuthedUserSummary();
  const email = userSummary?.private.email ?? '';

  const resolvedFlow =
    flow ?? (userSummary?.private.emailVerificationStatus as EmailVerificationStatus | undefined);
  // Until the flow is known, skipping and logging out are not yet distinguishable,
  // so neither is offered rather than guessing at the optional one.
  const isFlowResolved = resolvedFlow !== undefined;
  const isCompulsory = resolvedFlow === EmailVerificationStatus.COMPULSORY;

  const [isVerified, setIsVerified] = useState(false);
  const [serverErrorMessage, setServerErrorMessage] = useState('');
  const [secondsUntilResend, setSecondsUntilResend] = useState(RESEND_COOLDOWN_SECONDS);
  const hasReportedCodeEntry = useRef(false);
  const codeInputRef = useRef<HTMLInputElement | null>(null);

  const { mutate: generateCode, isLoading: isResending } =
    useApiAccountEmailVerificationGenerateCreate();
  const { mutate: redeemCode, isLoading: isVerifying } =
    useApiAccountEmailVerificationRedeemCreate();

  const trackEvent = useCallback(
    (
      Event: new (
        eventProperties: EmailVerificationEventProperties,
        observabilityTags: EmailVerificationObservabilityTags
      ) => AnalyticsEvent,
      extras: Omit<EmailVerificationEventProperties, 'flow' | 'flowSource'> = {}
    ) => {
      const flowTag = resolvedFlow ?? '';
      emitAnalyticsEvent(
        new Event(
          { flowSource: source, flow: flowTag, ...extras },
          {
            flow: flowTag,
            platform: isReactNative() ? 'native' : 'web',
            ...(extras.submitStatus ? { submitStatus: extras.submitStatus } : {}),
          }
        )
      );
    },
    [resolvedFlow, source]
  );

  // A code is sent on arrival so the user never has to ask for the first one.
  useEffect(() => {
    generateCode(
      { data: { refresh: false } },
      {
        onError: (error) =>
          setServerErrorMessage(readErrorMessage(error, i18n._(sendFailedMessage))),
      }
    );
    trackEvent(VerifyEmailPageLoad);
    // Mount-only on purpose: re-running would mail the user a second code.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (secondsUntilResend <= 0) return undefined;

    const timer = setTimeout(() => setSecondsUntilResend((seconds) => seconds - 1), 1000);
    return () => clearTimeout(timer);
  }, [secondsUntilResend]);

  useEffect(() => {
    if (!isVerified) return undefined;

    const timer = setTimeout(exit, VERIFIED_DISMISS_MS);
    return () => clearTimeout(timer);
  }, [isVerified, exit]);

  const schema = useMemo(
    () =>
      z.object({
        code: z
          .string()
          .min(1)
          .refine((value) => /^[a-zA-Z0-9]+$/.test(value.trim()), {
            // A custom refine message renders verbatim, so it is translated here.
            message: t`This code is incorrect. The code is case sensitive and must only contain alphanumeric characters (A-Z and 0-9). Check your email and try again.`,
          }),
      }),
    [t]
  );
  type FormData = DataTypeOfZodSchema<typeof schema>;

  const handleValidSubmit = useCallback(
    ({ code }: FormData) => {
      const trimmedCode = code.trim();
      setServerErrorMessage('');

      redeemCode(
        { data: { code: trimmedCode } },
        {
          onSuccess: () => {
            setIsVerified(true);
            announceVerified(i18n._(verifiedMessage));
            trackEvent(VerifyEmailSubmit, { submitStatus: 'success' });
          },
          onError: (error) => {
            const errorMessage = readErrorMessage(
              error,
              t`Something went wrong. Please try again.`
            );
            setServerErrorMessage(errorMessage);
            trackEvent(VerifyEmailSubmit, { errorMessage, submitStatus: 'fail' });
          },
        }
      );
    },
    [redeemCode, trackEvent, i18n, t]
  );

  const { configureField, formProps, reset, setValue, submitButtonProps, watch } = useKibbleForm({
    schema,
    onValidSubmit: handleValidSubmit,
  });

  // A code typed into the server-rendered markup arrives without a change event,
  // so the form is told about it once it is live, rather than losing the entry.
  useEffect(() => {
    const typedCode = codeInputRef.current?.value;
    if (typedCode) setValue('code', typedCode, { shouldDirty: true, shouldValidate: true });
  }, [setValue]);

  const handleResendCode = useCallback(() => {
    setServerErrorMessage('');
    // The field is cleared and the cooldown started only once a new code is really on
    // its way, so a failed resend leaves the code the user already has still usable
    // and lets them retry without waiting the cooldown out.
    generateCode(
      { data: { refresh: true } },
      {
        onSuccess: () => {
          reset({ code: '' });
          setSecondsUntilResend(RESEND_COOLDOWN_SECONDS);
        },
        onError: (error) =>
          setServerErrorMessage(readErrorMessage(error, i18n._(sendFailedMessage))),
      }
    );
    trackEvent(VerifyEmailResendCode);
  }, [generateCode, reset, trackEvent, i18n]);

  const handleCodeChange = useCallback(() => {
    // The server message describes the previous attempt, so it goes as the code changes.
    setServerErrorMessage('');

    if (!hasReportedCodeEntry.current) {
      hasReportedCodeEntry.current = true;
      trackEvent(VerifyEmailAddCode);
    }
  }, [trackEvent]);

  const handleSkip = useCallback(() => {
    trackEvent(VerifyEmailSkip);
    exit();
  }, [exit, trackEvent]);

  const handleLogout = useCallback(() => {
    trackEvent(VerifyEmailLogout);
    logout();
  }, [logout, trackEvent]);

  // `isDirty` stays true once the field has been touched, so the submit follows the
  // value itself and goes back to disabled when the code is cleared.
  const hasCode = Boolean(watch('code')?.trim());
  const isCountingDown = secondsUntilResend > 0;
  const resendButtonText = isCountingDown
    ? i18n._(resendCountdownLabel(secondsUntilResend))
    : t`Send new code`;
  const isBusy = isVerifying || isResending;

  return (
    <PageChrome isEmbedded={isEmbedded}>
      <ImageHeader
        heading={headerText(i18n, isCompulsory)}
        headingSize="400"
        image={verifyEmailImage}
        description={t`Paper airplane doodle`}
        resizeMethod="contain"
        // next/image only honours a "100%" width when the height is a percentage too,
        // so the width is restated as a style to stop the image falling back to its
        // intrinsic size and sitting off to one side.
        imageProps={{ sx: { width: '100%' } }}
      >
        <Paragraph mb="0x">{descriptionText(i18n, email, isCompulsory)}</Paragraph>
      </ImageHeader>

      <Form {...formProps}>
        <TextField
          {...configureField('code')}
          required={false}
          // Mailing a code does not stop the user typing one: they may already have an
          // earlier code in hand, and on web they can be mid-entry in the server-rendered
          // field when the arrival request is still in flight. Only redeeming the code,
          // which is about to replace the field anyway, locks it.
          disabled={isVerifying || isVerified}
          primaryLabel={t`Verification code`}
          placeholder={t`Enter the code from email`}
          translatedFieldErrorMessage={serverErrorMessage}
          onChange={handleCodeChange}
          webInputRef={(element) => {
            codeInputRef.current = element as HTMLInputElement | null;
          }}
        />

        {isVerified ? (
          <Flex
            flexDirection="row"
            alignItems="center"
            justifyContent="center"
            mt="12x"
            webProps={{ role: 'status', 'aria-live': 'polite' }}
            rxnProps={{ accessibilityRole: 'alert', accessibilityLiveRegion: 'polite' }}
          >
            <Check fill={colors.text.success.toString()} width="18px" height="18px" aria-hidden />
            <Text fontWeight="semibold" ml="2x" textColor="success">
              {i18n._(verifiedMessage)}
            </Text>
          </Flex>
        ) : (
          <Flex flexDirection="column" flexGrow="1" justifyContent="flex-end">
            <Button
              {...submitButtonProps}
              variant="primary"
              fullWidth
              loading={isVerifying}
              disabled={isBusy || !hasCode}
              testID="email-verification-submit"
            >
              {t`Verify your email`}
            </Button>
            <Button
              mt="2x"
              variant="flat"
              fullWidth
              onPress={handleResendCode}
              loading={isResending}
              disabled={isBusy || isCountingDown}
              testID="email-verification-resend"
            >
              {resendButtonText}
            </Button>
            {isFlowResolved &&
              (isCompulsory ? (
                <Button
                  mt="2x"
                  variant="flat"
                  fullWidth
                  disabled={isBusy}
                  onPress={handleLogout}
                  testID="email-verification-logout"
                >
                  {t`Log out`}
                </Button>
              ) : (
                <Button
                  mt="2x"
                  variant="flat"
                  fullWidth
                  disabled={isBusy}
                  onPress={handleSkip}
                  testID="email-verification-skip"
                >
                  {t`Skip for now`}
                </Button>
              ))}
          </Flex>
        )}
      </Form>
    </PageChrome>
  );
};

export default EmailVerificationPage;
